import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
} from "@mui/material";
import { createStyleSheet, globalStyles } from "../utils/styles";
import { useCallback, useEffect, useState } from "react";
import CollapseIcon from "@mui/icons-material/ExpandLess";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import IconButton from "./IconButton";
import React from "react";
import { TaskState } from "../features/data/patientApiSlice";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";
import LightTooltip from "./LightTooltip";

const EntryContainer = (props: {
  id?: string;
  containing?: boolean;
  contained?: boolean;
  titleIcon: React.ReactNode;
  titleText: string;
  titleTextPost?: string;
  subtitleText: string;
  extraTitleComponent?: React.ReactNode;
  cornerTitleComponent?: React.ReactNode;
  headerComponent?: React.ReactNode;
  expandByDefault?: boolean;
  onExpandClicked?: () => void;
  onExpanded?: () => void;
  menuComponent?: React.ReactNode;
  taskStateIndicator?: TaskState;
  selected?: boolean;
  isFetching: boolean;
  errorText: string;
  bodyComponent?: React.ReactNode;
  isSaving?: boolean;
  tooltipBody?: React.ReactNode;
  completeTaskComponent?: React.ReactNode;
  showToggle?: boolean | undefined;
}) => {
  const {
    id,
    containing,
    contained,
    titleIcon,
    titleText,
    titleTextPost,
    subtitleText,
    extraTitleComponent,
    cornerTitleComponent,
    headerComponent,
    expandByDefault,
    onExpandClicked,
    onExpanded,
    menuComponent,
    taskStateIndicator,
    selected,
    isFetching,
    errorText,
    bodyComponent,
    isSaving,
    tooltipBody,
    completeTaskComponent,
    showToggle,
  } = props;
  const { t } = useTranslation();

  // Component state
  const [expandedView, setExpandedView] = useState(!!expandByDefault);

  // Callbacks
  const handleExpandViewClick = useCallback(
    (forceExpand: boolean) => () => {
      const isSelected = selected;
      onExpandClicked?.();
      if (containing || isSelected || forceExpand) {
        setExpandedView(!expandedView);
      }
    },
    [containing, selected, onExpandClicked, expandedView]
  );

  // Reactive code
  useEffect(() => {
    if (expandByDefault) {
      setExpandedView(true);
    }
  }, [expandByDefault]);
  useEffect(() => {
    if (expandedView) {
      onExpanded?.();
    }
  }, [expandedView, onExpanded]);

  const showAsTodo = taskStateIndicator === TaskState.todo;
  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box id={id} sx={styles.spacerBox} />
      <Box
        sx={
          {
            ...styles.container,
            ...(contained ? styles.contained : styles.nonContained),
            ...(contained &&
              showAsTodo && {
                backgroundColor: (theme) => theme.palette.detailBackground.main,
              }),
            ...(contained &&
              (selected ? styles.selectedBorder : styles.unselectedBorder)),
          } as SxProps<Theme>
        }
      >
        {showAsTodo && !contained ? (
          <Box
            sx={{
              ...styles.taskTypeBar,
              backgroundColor: (theme) => theme.palette.primary.main,
            }}
          />
        ) : null}
        <Box
          sx={
            {
              ...styles.contentContainer,
              ...(!containing &&
                !contained &&
                (selected ? styles.selectedBorder : styles.unselectedBorder)),
            } as SxProps<Theme>
          }
        >
          <Box
            sx={styles.headerContainer}
            onClick={handleExpandViewClick(true)}
          >
            <Stack direction="row" sx={styles.titleRow}>
              {titleIcon}
              <Box sx={styles.titleContainer}>
                <LightTooltip
                  title={tooltipBody}
                  enterTouchDelay={0}
                >
                  <Typography
                    variant="detailEntryTitle"
                    sx={{
                      ...(showAsTodo && {
                        color: (theme) => theme.palette.primary.main,
                      }),
                    }}
                  >
                    {titleText}
                    {titleTextPost ? (
                      <Typography
                        variant="detailEntryBody"
                        sx={{
                          ...(showAsTodo && {
                            color: (theme) => theme.palette.primary.main,
                          }),
                        }}
                      >
                        &nbsp;&nbsp;{titleTextPost}
                      </Typography>
                    ) : null}
                  </Typography>
                </LightTooltip>
                <Typography variant="detailEntryBody">{subtitleText}</Typography>
              </Box>
              {extraTitleComponent}
              <Box sx={globalStyles.flexSpace} />
              {headerComponent}
              {showToggle !== false && (
                <IconButton
                  icon={expandedView ? <CollapseIcon /> : <ExpandIcon />}
                  tooltip={t("common:" + (expandedView ? "collapse" : "expand"))}
                  onClick={handleExpandViewClick(true)}
                />
              )}
              {menuComponent}
              {cornerTitleComponent ? (
                <Box sx={styles.cornerTitleComponent}>{cornerTitleComponent}</Box>
              ) : null}
            </Stack>
            {completeTaskComponent}
          </Box>
          {expandedView ? (
            isFetching ? (
              <>
                <Box sx={styles.progressCover}>
                  <CircularProgress />
                </Box>
                {bodyComponent ? (
                  <Stack direction="column">
                    <Divider />
                    <Box sx={styles.taskDetailContainer}>{bodyComponent}</Box>
                  </Stack>
                ) : null}
              </>
            ) : errorText.length > 0 ? (
              <Typography variant="errorBody">{errorText}</Typography>
            ) : bodyComponent ? (
              <Stack direction="column">
                <Divider />
                <Box sx={styles.taskDetailContainer}>{bodyComponent}</Box>
              </Stack>
            ) : null
          ) : null}
          {isSaving ? (
            <Box
              sx={{
                ...styles.progressCover,
                backgroundColor: (theme) =>
                  alpha(theme.palette.common.black, 0.4),
              }}
            >
              <CircularProgress style={{ color: "white" }} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const styles = createStyleSheet({
  container: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    //scrollMargin: "8px", // Doesn't seem to work
    //scrollSnapAlign: "start",
  },
  nonContained: {
    borderRadius: 1,
    boxShadow: 1,
    backgroundColor: "white",
  },
  contained: {
    mx: -2,
    marginBottom: -2,
    borderTop: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
  },
  selectedBorder: {
    border: (theme) => `3px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
  unselectedBorder: {
    border: "3px solid transparent",
  },
  taskTypeBar: {
    width: "4px",
    alignSelf: "stretch",
  },
  contentContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  headerContainer: {
    p: 2,
    cursor: "pointer",
    position: "relative",
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
    marginX: 1,
  },
  titleRow: {
    width: "100%",
  },
  spacerBox: {
    height: (theme: Theme) => theme.spacing(2),
  },
  extraTitleText: {
    borderRadius: 1000,
    height: 30,
    marginLeft: 1,
    px: 2,
    display: "flex",
    alignItems: "center",
  },
  cornerTitleComponent: {
    position: "absolute",
    top: 0,
    right: 0,
    pointerEvents: "none",
  },
  taskDetailContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    mx: 2,
    marginBottom: 2,
    [theme.breakpoints.down("desktop")]: {
      mx: 1.5,
      marginBottom: 1.5,
    },
  },
  progressCover: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    backgroundColor: (theme) => alpha(theme.palette.common.white, 0.4),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default EntryContainer;
